import {
  addDays,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns"
import { DateRange } from "react-day-picker"

const DATE_PRESET_VALUES = [
  "today",
  "tomorrow",
  "thisWeek",
  "nextWeek",
  "currentWeek",
  "lastWeek",
  "lastMonth",
  "lastSixMonths",
  "lastYear",
  "currentMonth",
  "currentYear",
] as const

// Doubles as the translation key under `ui.datePresets`
export type DatePresetValue = (typeof DATE_PRESET_VALUES)[number]

export interface DateRangePreset {
  value: DatePresetValue
}

export const STANDARD_PRESETS: DateRangePreset[] = [
  { value: "today" },
  { value: "tomorrow" },
  { value: "thisWeek" },
  { value: "nextWeek" },
]

export const STATS_PRESETS: DateRangePreset[] = [
  { value: "currentWeek" },
  { value: "lastWeek" },
  { value: "lastMonth" },
  { value: "lastSixMonths" },
  { value: "lastYear" },
  { value: "currentMonth" },
  { value: "currentYear" },
]

/** The current calendar month. */
export const getCurrentMonthRange = (): { from: Date; to: Date } => {
  const today = new Date()
  return { from: startOfMonth(today), to: endOfMonth(today) }
}

const isDatePresetValue = (value: string): value is DatePresetValue =>
  DATE_PRESET_VALUES.some((presetValue) => presetValue === value)

const presetResolvers: Record<DatePresetValue, () => DateRange> = {
  today: () => {
    const today = new Date()
    return { from: startOfDay(today), to: endOfDay(today) }
  },
  tomorrow: () => {
    const tomorrow = addDays(new Date(), 1)
    return { from: startOfDay(tomorrow), to: endOfDay(tomorrow) }
  },
  thisWeek: () => {
    const today = new Date()
    return {
      from: startOfDay(startOfWeek(today, { weekStartsOn: 1 })),
      to: endOfDay(endOfWeek(today, { weekStartsOn: 1 })),
    }
  },
  nextWeek: () => {
    const nextWeekStart = addWeeks(new Date(), 1)
    return {
      from: startOfDay(startOfWeek(nextWeekStart, { weekStartsOn: 1 })),
      to: endOfDay(endOfWeek(nextWeekStart, { weekStartsOn: 1 })),
    }
  },
  currentWeek: () => {
    const today = new Date()
    return {
      from: startOfDay(startOfWeek(today, { weekStartsOn: 1 })),
      to: endOfDay(endOfWeek(today, { weekStartsOn: 1 })),
    }
  },
  lastWeek: () => {
    const lastWeek = subDays(new Date(), 7)
    return {
      from: startOfDay(startOfWeek(lastWeek, { weekStartsOn: 1 })),
      to: endOfDay(endOfWeek(lastWeek, { weekStartsOn: 1 })),
    }
  },
  lastMonth: () => {
    const lastMonth = subMonths(new Date(), 1)
    return {
      from: startOfDay(startOfMonth(lastMonth)),
      to: endOfDay(endOfMonth(lastMonth)),
    }
  },
  lastSixMonths: () => {
    const today = new Date()
    return {
      from: startOfDay(startOfMonth(subMonths(today, 6))),
      to: endOfDay(endOfMonth(today)),
    }
  },
  lastYear: () => {
    const lastYear = subYears(new Date(), 1)
    return {
      from: startOfDay(startOfYear(lastYear)),
      to: endOfDay(endOfYear(lastYear)),
    }
  },
  currentMonth: () => getCurrentMonthRange(),
  currentYear: () => {
    const today = new Date()
    return {
      from: startOfDay(startOfYear(today)),
      to: endOfDay(endOfYear(today)),
    }
  },
}

export function getPresetDateRange(preset: string): DateRange | undefined {
  if (!isDatePresetValue(preset)) {
    return undefined
  }
  return presetResolvers[preset]()
}
